import lodashDifference from 'lodash-es/difference';
import lodashSnakeCase from 'lodash-es/snakeCase';

import { eventTypeStandard, getPageType, push } from './internal/core';
import { formatTrackingListName, formatSortType } from './internal/format';
import { pushWithGlobalSales } from './internal/pushWithGlobal';
import { formatSale } from './internal/saleFormat';

import { apiTrackSaleView, SellingMethod } from '~/apiClient';
import { EMPTY_OPTION_VALUE } from '~/constants/filters';
import type { PlatformSaleListingItemEntity } from '~/types/sale.type';

export type UseTrackingSlpReturn = {
    trackAuctionsButton: VoidFunction;
    trackBuyNowButton: VoidFunction;
    trackViewSaleList: (sales: PlatformSaleListingItemEntity[]) => void;
    trackSlpSortBy: (sortSelectModel: string) => void;
    trackSlpResetFilters: VoidFunction;
    trackSlpSelectSale: VoidFunction;

    onSlpFilterChangeByModel: (name: string, model: (string | number)[], value: (string | number)[]) => void;
    onSlpFilterChangeByValue: (name: string | string[], value: unknown) => void;
    onSlpFilterChangeByKey: (name: string | string[], options: { value: unknown; label: string }[], key: unknown) => void;
};

export const useTrackingSlp = (): UseTrackingSlpReturn => {
    const route = useRoute();
    const { locale } = useTypedI18n();

    const trackSlpFilterClick = (name: string, value: unknown): void => {
        pushWithGlobalSales((sales, listName) => ({
            event: 'click',
            click_element: 'filter',
            filter_name: name,
            filter_value: value,
            page_type: getPageType(),
            list_name: listName,
            sales,
            ...eventTypeStandard,
        }));
    };

    const onSlpFilterChangeByValue = (name: string | string[], value: unknown): void => {
        const _name = lodashSnakeCase((Array.isArray(name) ? name : [name]).join('_'));
        trackSlpFilterClick(_name, value === EMPTY_OPTION_VALUE ? null : value);
    };

    return {
        trackAuctionsButton(): void {
            pushWithGlobalSales((sales, listName) => ({
                event: 'click',
                click_element: 'button',
                click_text: 'auctions',
                page_type: getPageType(),
                list_name: listName,
                sales,
                ...eventTypeStandard,
            }));
        },

        trackBuyNowButton(): void {
            pushWithGlobalSales((sales, listName) => ({
                event: 'click',
                click_element: 'button',
                click_text: 'buy_now',
                page_type: getPageType(),
                list_name: listName,
                sales,
                ...eventTypeStandard,
            }));
        },

        trackViewSaleList(sales): void {
            const listName = formatTrackingListName('page');

            push({
                event: 'view_sale_list',
                page_type: getPageType(),
                list_name: listName,
                count: sales.length,
                sales: sales.map((sale, index) => formatSale(sale, index, listName)),
                ...eventTypeStandard,
            });

            if (sales.length > 0) {
                apiTrackSaleView({
                    sales: sales.map(sale => {
                        return sale.sellingMethod === SellingMethod.Auction ? { auctionId: sale.id } : { directSaleId: sale.id };
                    }),
                    language: localeToLanguage(locale.value),
                    urlPath: route.fullPath,
                });
            }
        },

        trackSlpSortBy(sortSelectModel): void {
            pushWithGlobalSales((sales, listName) => ({
                event: 'click',
                click_element: 'sort_by',
                sort_type: formatSortType(sortSelectModel),
                page_type: getPageType(),
                list_name: listName,
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSlpResetFilters(): void {
            pushWithGlobalSales((sales, listName) => ({
                event: 'click',
                click_element: 'button',
                click_text: 'reset_filters',
                page_type: getPageType(),
                list_name: listName,
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSlpSelectSale(): void {
            pushWithGlobalSales((sales, listName) => ({
                event: 'select_sale',
                page_type: getPageType(),
                list_name: listName,
                sales,
                ...eventTypeStandard,
            }));
        },

        onSlpFilterChangeByModel(name, model, value): void {
            lodashDifference(model, value).map(key => onSlpFilterChangeByValue([name, String(key)], false));
            lodashDifference(value, model).map(key => onSlpFilterChangeByValue([name, String(key)], true));
        },

        onSlpFilterChangeByValue,

        onSlpFilterChangeByKey(name, options, key): void {
            const _name = lodashSnakeCase((Array.isArray(name) ? name : [name]).join('_'));
            const value = key === EMPTY_OPTION_VALUE ? null : options.find(o => o.value === key)?.label;
            trackSlpFilterClick(_name, value);
        },
    };
};
